import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ChatWidget.module.css';

const CHAT_ENDPOINT =
    process.env.REACT_APP_CHAT_ENDPOINT || 'https://agents.soflib.com/message';

const ChatIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
);

const ChatWidget = () => {
    const { t, i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const bodyRef = useRef(null);

    const toggleChat = () => setIsOpen((prev) => !prev);

    useEffect(() => {
        if (bodyRef.current) {
            bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        }
    }, [messages, loading]);

    const sendMessage = async (text) => {
        const trimmed = text.trim();
        if (!trimmed || loading) return;

        setMessages((prev) => [...prev, { role: 'user', text: trimmed }]);
        setInput('');
        setLoading(true);

        try {
            const res = await fetch(CHAT_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: trimmed,
                    business_model: 'soltarc-service',
                    language: i18n.language,
                }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json().catch(() => ({}));
            const reply =
                data.reply ||
                data.message ||
                data.response ||
                t('bubble_chat.no_reply');
            setMessages((prev) => [...prev, { role: 'bot', text: reply }]);
        } catch {
            setMessages((prev) => [
                ...prev,
                { role: 'bot', text: t('bubble_chat.error') },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        sendMessage(input);
    };

    return (
        <div className={styles.chatWidget}>
            {isOpen && (
                <div
                    className={styles.chatWindow}
                    role="dialog"
                    aria-label={t('bubble_chat.title')}
                >
                    <div className={styles.chatHeader}>
                        <span className={styles.chatHeaderTitle}>
                            {t('bubble_chat.title')}
                        </span>
                        <button
                            onClick={toggleChat}
                            className={styles.closeBtn}
                            aria-label={t('bubble_chat.close')}
                        >
                            ×
                        </button>
                    </div>

                    <div className={styles.chatBody} ref={bodyRef}>
                        {messages.length === 0 && !loading && (
                            <p className={styles.placeholder}>
                                {t('bubble_chat.welcome_message')}
                            </p>
                        )}
                        {messages.map((m, i) => (
                            <div
                                key={i}
                                className={
                                    m.role === 'user' ? styles.msgUser : styles.msgBot
                                }
                            >
                                {m.text}
                            </div>
                        ))}
                        {loading && (
                            <div className={styles.msgBot}>
                                <span className={styles.typing}>…</span>
                            </div>
                        )}
                    </div>

                    <form onSubmit={handleSubmit} className={styles.chatInput}>
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={t('bubble_chat.write_message')}
                            aria-label={t('bubble_chat.write_message')}
                            disabled={loading}
                        />
                    </form>
                </div>
            )}
            <button
                className={styles.chatToggle}
                onClick={toggleChat}
                aria-label={isOpen ? t('bubble_chat.close') : t('bubble_chat.open')}
                aria-expanded={isOpen}
            >
                {isOpen ? '×' : <ChatIcon />}
            </button>
        </div>
    );
};

export default ChatWidget;
